import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import type { EpisodeTO } from '../../lib';
import { compareDateString } from '../helpers';

const useLatestEpisode = (): {
  latestEpisode: Pick<
    EpisodeTO,
    | 'details'
    | 'episode_closed_date'
    | 'episode_closed'
    | 'episode_no'
    | 'episode_opened_date'
  > | null;
  open: boolean;
} => {
  const { latestEpisodeId } = useSelector((state: RootState) => state.timeline);
  const { patientProfile } = useSelector(
    (state: RootState) => state.patientProfile,
  );

  const clinics = patientProfile.clinics || [];
  if (!latestEpisodeId) {
    return { latestEpisode: null, open: false };
  }

  // Sort episodes by opened date in descending order
  const allOpenedEpisodes = [...clinics]
    .flatMap((clinic) => clinic.episodes || [])
    .sort((a, b) =>
      compareDateString(
        b.episode_opened_date || '',
        a.episode_opened_date || '',
      ),
    );

  // Get the latest opened episode
  if (allOpenedEpisodes.length) {
    const latestOpenEpisode = allOpenedEpisodes[0] || null;

    return { latestEpisode: latestOpenEpisode, open: true };
  }

  // Otherwise find all the clinics' the most recent episode_closed_date
  const allClinics = [...clinics].sort((a, b) =>
    compareDateString(b.episode_closed_date || '', a.episode_closed_date || ''),
  );
  if (allClinics.length) {
    const latestCloseEpisodeClinic = allClinics[0] || null;

    return {
      latestEpisode: {
        episode_closed_date: latestCloseEpisodeClinic.episode_closed_date || '',
        episode_closed: latestCloseEpisodeClinic.episode_closed || false,
        episode_opened_date: latestCloseEpisodeClinic.episode_opened_date || '',
        episode_no: latestCloseEpisodeClinic.episode_no || '',
      },
      open: false,
    };
  }

  return { latestEpisode: null, open: false };
};

export default useLatestEpisode;
